'use client'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useProductStock } from 'product/services/productStock/client/useProductStock'
import type { Product, ProductSize } from 'product/types'
import type { Stock, StockAvailability } from 'product/types/Stock'
import { useMemo } from 'react'

interface UseProductColorsProps {
	product: Product
	productId: string
	selectedColorId: string
	defaultWareHouseId?: string
	delayedWareHouse?: (warehousesId?: string[]) => boolean
	preComputedStock?: Stock
}

export interface DelayedSize {
	isDelayed: boolean
}

const getAvailabilityInfo = (
	size: ProductSize,
	selectedStockSizes?: Record<string, StockAvailability>,
	delayedWareHouse?: (warehousesId?: string[]) => boolean
): {
	available: boolean
	isDelayed: boolean
	warehouses?: string[]
	lastUnits?: boolean
} => {
	if (!selectedStockSizes?.[size.id]) {
		return {
			available: true,
			isDelayed: false,
		}
	}
	const info = {
		available: selectedStockSizes[size.id].available,
		warehouses: selectedStockSizes[size.id].warehouses,
		lastUnits: selectedStockSizes[size.id].lastUnits,
		isDelayed: false,
	}
	if (delayedWareHouse) {
		info.isDelayed = delayedWareHouse(selectedStockSizes[size.id].warehouses)
	}
	return info
}

export function useProductSelectedColorSizes({
	product,
	productId,
	selectedColorId,
	preComputedStock,
	delayedWareHouse,
}: UseProductColorsProps): {
	sizes: (ProductSize & StockAvailability & DelayedSize)[]
	selectedProductId: string
} {
	const {
		country: { countryISO },
	} = useMasterData()

	const { data: productStock } = useProductStock({
		productId,
		country: countryISO,
		enabled: !preComputedStock,
	})

	const productColors = useMemo(() => product?.colors, [product])
	const selectedColor = useMemo(
		() => productColors?.find((item) => item.id === selectedColorId),
		[productColors, selectedColorId]
	)

	const currentStock = useMemo(
		() => preComputedStock ?? productStock,
		[productStock, preComputedStock]
	)

	const selectedStockSizes = useMemo(
		() => currentStock?.colors[selectedColorId]?.sizes,
		[selectedColorId, currentStock]
	)

	const sizes: (ProductSize & StockAvailability & DelayedSize)[] =
		useMemo(() => {
			if (!selectedColor) {
				return []
			}

			return (
				selectedColor.sizes
					/**
					 * Filter sizes that are available in stock.
					 *
					 * In case of related sizes we have two options:
					 * - Set preComputedStock with the related sizes
					 * - Filter only the sizes that are available in stock
					 */
					.filter((size) => !!selectedStockSizes?.[size.id])
					.map((size) => ({
						...size,
						...getAvailabilityInfo(size, selectedStockSizes, delayedWareHouse),
					}))
			)
		}, [selectedColor, selectedStockSizes, delayedWareHouse])

	return useMemo(
		() => ({
			sizes,
			selectedProductId: selectedColor?.relatedProductId ?? productId,
		}),
		[productId, selectedColor, sizes]
	)
}
